<template>
  <div class="flex flex-col gap-3 bg-white rounded border border-gray-200">
    <!-- Header with Contract Filter -->
    <ComponentHeader title="Portfolio Performance Analysis (Alpha 0.1)">
      <!-- Contract Filter -->
      <div v-if="showData && availableContracts.length > 0" class="flex items-center gap-2">
        <label class="text-xs font-medium text-gray-700">Filter by Contract:</label>
        <select
          v-model="selectedContractFilter"
          class="px-2 py-1 border border-gray-300 rounded text-xs bg-white min-w-[180px]"
        >
          <option value="">All Contracts</option>
          <option v-for="contract in availableContracts" :key="contract.id" :value="contract.id">
            {{ contract.name || contract.id }}
          </option>
        </select>
        <span v-if="selectedContractFilter" class="text-xs text-gray-500">
          ({{ Object.keys(entityData).length }} entities)
        </span>
      </div>
    </ComponentHeader>

    <div v-if="!showData">
      <LoadingIndicator class="p-4" />
    </div>

    <div v-else class="p-4">
      <!-- Table 2: Metrics Display (Entity-specific or Weighted Average) -->
      <div class="bg-blue-50 rounded border border-blue-200 mb-3">
        <ComponentHeader
          :title="selectedEntity ? `Metrics Display (${selectedEntity.entityName || selectedEntity.entityId})` : 'Metrics Display (Weighted Average - All Entities)'"
          :heading-level="3"
        >
          <button
            v-if="selectedEntity"
            @click="selectedEntity = null"
            class="btn-danger-sm"
          >
            Clear Selection
          </button>
        </ComponentHeader>

        <div class="p-3">
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-300 text-xs">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-2 py-1 border text-left">Year</th>
                <th class="px-2 py-1 border text-right">AWV Rate</th>
                <th class="px-2 py-1 border text-right">Attribution</th>
                <th class="px-2 py-1 border text-right">Total PMPY</th>
                <th class="px-2 py-1 border text-right">Risk (HCC)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in metricsTableData" :key="row.year">
                <td class="px-2 py-1 border font-semibold">{{ row.year === 2025 ? '2025 Q2' : row.year }}</td>
                <td class="px-2 py-1 border text-right">{{ (row.awv_rate * 100).toFixed(2) }}%</td>
                <td class="px-2 py-1 border text-right">{{ row.attribution.toLocaleString() }}</td>
                <td class="px-2 py-1 border text-right">${{ row.total_pmpy.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                <td class="px-2 py-1 border text-right">{{ row.risk !== null && row.risk !== undefined ? row.risk.toFixed(3) : 'N/A' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>

      <!-- Regression Settings -->
      <div class="bg-purple-50 rounded border border-purple-200 mb-3">
        <ComponentHeader
          title="Regression Settings"
          :heading-level="3"
          :collapsible="true"
          v-model:expanded="showRegressionSettings"
        />

        <div v-show="showRegressionSettings" class="p-3">
        <!-- National Benchmarks -->
        <h4 class="text-xs font-semibold text-gray-800 mb-1">National Benchmarks (for Leverage Index)</h4>
        <div class="grid grid-cols-2 gap-3">
          <div class="bg-white p-2 rounded border">
            <label class="text-xs text-gray-600 mb-1 block">National PMPY Slope Benchmark ($)</label>
            <input
              v-model.number="nationalPmpyBenchmark"
              type="number"
              step="0.01"
              class="w-full px-2 py-1 border border-gray-300 rounded text-xs"
            />
          </div>
          <div class="bg-white p-2 rounded border">
            <label class="text-xs text-gray-600 mb-1 block">Average National PMPY ($)</label>
            <input
              v-model.number="avgNationalPmpy"
              type="number"
              step="0.01"
              class="w-full px-2 py-1 border border-gray-300 rounded text-xs"
            />
          </div>
        </div>
        <div class="text-xs text-gray-600 mt-2">
          <div class="font-semibold mb-1">Leverage Index Formula:</div>
          <div class="bg-white p-2 rounded border mb-2 font-mono text-xs">
            Leverage Index = (PMPY Slope - National PMPY Benchmark) × Attribution - (Risk Slope × Avg National PMPY × Attribution)
          </div>
          <div class="space-y-1">
            <div><span class="font-semibold">Component 1 (Cost Trend Impact):</span> (PMPY Slope - National PMPY Benchmark) × Attribution</div>
            <div class="ml-3">• Measures the dollar impact of an entity's PMPY cost trend vs. national benchmark</div>
            <div class="ml-3">• Positive = costs growing faster than national benchmark (unfavorable)</div>
            <div class="ml-3">• Multiplied by attribution to scale the impact by entity size</div>

            <div class="mt-2"><span class="font-semibold">Component 2 (Risk Score Impact):</span> Risk Slope × Avg National PMPY × Attribution</div>
            <div class="ml-3">• Measures the dollar impact of rising risk scores on benchmark payments</div>
            <div class="ml-3">• Higher risk scores increase benchmark payments, which can offset cost growth</div>
            <div class="ml-3">• This favorable impact is subtracted from Component 1</div>

            <div class="mt-2"><span class="font-semibold">Interpretation:</span></div>
            <div class="ml-3">• <span class="text-red-600 font-semibold">Positive Leverage Index</span> = Net unfavorable trend (costs growing faster than benchmarks + risk adjustments)</div>
            <div class="ml-3">• <span class="text-green-600 font-semibold">Negative Leverage Index</span> = Net favorable trend (cost growth controlled relative to benchmarks + risk adjustments)</div>
            <div class="ml-3">• Magnitude indicates total dollar impact scaled by attribution</div>
          </div>
        </div>
        </div>
      </div>

      <!-- Portfolio-Level Regression Stats -->
      <div v-if="portfolioAverageSlopes" class="bg-yellow-50 rounded border border-yellow-200 mb-3">
        <ComponentHeader
          title="Portfolio-Level Regression Slopes"
          subtitle="2022-2024"
          :heading-level="3"
          :collapsible="true"
          v-model:expanded="showPortfolioSlopes"
        />

        <div v-show="showPortfolioSlopes" class="p-3">
        <div class="grid grid-cols-4 gap-3">
          <div class="bg-white p-2 rounded border">
            <div class="text-xs text-gray-600 mb-1">AWV Slope</div>
            <div class="text-sm font-semibold">
              {{ (portfolioAverageSlopes.awv >= 0 ? '+' : '') + (portfolioAverageSlopes.awv * 100).toFixed(2) }}%
              <span v-if="portfolioAverageSlopes.awvPValue !== null && portfolioAverageSlopes.awvPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
            </div>
            <div class="text-xs text-gray-500 mt-1">
              p = {{ portfolioAverageSlopes.awvPValue !== null ? portfolioAverageSlopes.awvPValue.toFixed(4) : 'N/A' }}
            </div>
          </div>
          <div class="bg-white p-2 rounded border">
            <div class="text-xs text-gray-600 mb-1">Attribution Slope</div>
            <div class="text-sm font-semibold">
              {{ (portfolioAverageSlopes.attrib >= 0 ? '+' : '') + portfolioAverageSlopes.attrib.toFixed(2) }}%
              <span v-if="portfolioAverageSlopes.attribPValue !== null && portfolioAverageSlopes.attribPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
            </div>
            <div class="text-xs text-gray-500 mt-1">
              p = {{ portfolioAverageSlopes.attribPValue !== null ? portfolioAverageSlopes.attribPValue.toFixed(4) : 'N/A' }}
            </div>
          </div>
          <div class="bg-white p-2 rounded border">
            <div class="text-xs text-gray-600 mb-1">PMPY Slope</div>
            <div class="text-sm font-semibold">
              {{ (portfolioAverageSlopes.pmpy >= 0 ? '+$' : '-$') + Math.abs(portfolioAverageSlopes.pmpy).toFixed(2) }}
              <span v-if="portfolioAverageSlopes.pmpyPValue !== null && portfolioAverageSlopes.pmpyPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
            </div>
            <div class="text-xs text-gray-500 mt-1">
              p = {{ portfolioAverageSlopes.pmpyPValue !== null ? portfolioAverageSlopes.pmpyPValue.toFixed(4) : 'N/A' }}
            </div>
          </div>
          <div class="bg-white p-2 rounded border">
            <div class="text-xs text-gray-600 mb-1">Risk Slope</div>
            <div class="text-sm font-semibold">
              {{ (portfolioAverageSlopes.risk >= 0 ? '+' : '') + portfolioAverageSlopes.risk.toFixed(3) }}
              <span v-if="portfolioAverageSlopes.riskPValue !== null && portfolioAverageSlopes.riskPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
            </div>
            <div class="text-xs text-gray-500 mt-1">
              p = {{ portfolioAverageSlopes.riskPValue !== null ? portfolioAverageSlopes.riskPValue.toFixed(4) : 'N/A' }}
            </div>
          </div>
        </div>
        <div class="text-xs text-gray-600 mt-2">
          Regression slopes calculated from portfolio-wide weighted averages across years. <span class="text-yellow-600 font-bold">*</span> = Statistically significant (p &lt; 0.05)
        </div>
        </div>
      </div>

      <!-- Table 1: Entity List -->
      <div class="bg-gray-50 rounded border border-gray-200">
        <ComponentHeader
          :title="`Entity List (${Object.keys(entityData).length} entities)`"
          subtitle="Slopes calculated using 2022-2024"
          :heading-level="3"
        >
          <div class="text-xs text-gray-600 flex flex-col items-end gap-1">
            <span>
              <span class="text-yellow-600 font-bold">*</span> = Statistically significant (p &lt; 0.05)
            </span>
            <span>
              <span class="text-green-600 font-semibold">Green</span> = Above portfolio avg (good) |
              <span class="text-red-600 font-semibold">Red</span> = Below portfolio avg (bad)
            </span>
            <span>
              Leverage Index: <span class="text-green-600 font-semibold">Negative</span> = Better than national benchmark |
              <span class="text-red-600 font-semibold">Positive</span> = Worse than national benchmark
            </span>
          </div>
        </ComponentHeader>

        <div class="p-3">
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-300 text-xs">
            <thead class="bg-gray-100">
              <tr>
                <th class="px-2 py-1 border text-left">Entity Type</th>
                <th class="px-2 py-1 border text-left">Entity Name</th>
                <th class="px-2 py-1 border text-left">InfoTags</th>
                <th class="px-2 py-1 border text-left">Metric Alerts</th>
                <th class="px-2 py-1 border text-right">2024 Attribution</th>
                <th class="px-2 py-1 border text-left">Workload Distribution</th>
                <th class="px-2 py-1 border text-right">AWV Slope</th>
                <th class="px-2 py-1 border text-right">Attrib Slope</th>
                <th class="px-2 py-1 border text-right">PMPY Slope</th>
                <th class="px-2 py-1 border text-right">Risk Slope</th>
                <th class="px-2 py-1 border text-right">Leverage Index</th>
                <th class="px-2 py-1 border text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entity in entityList"
                :key="entity.entityId"
                :class="{ 'bg-blue-100': selectedEntity && selectedEntity.entityId === entity.entityId }"
              >
                <td class="px-2 py-1 border">{{ entity.entityType }}</td>
                <td class="px-2 py-1 border">
                  <router-link
                    v-if="entity.entityDatabaseId"
                    :to="`/entity/${entity.entityDatabaseId}`"
                    class="text-blue-600 hover:text-blue-800 hover:underline"
                  >
                    <span class="sensitive">{{ entity.entityName || 'N/A' }}</span> <span class="font-mono text-gray-600">({{ entity.entityId }})</span>
                  </router-link>
                  <span v-else><span class="sensitive">{{ entity.entityName || 'N/A' }}</span> <span class="font-mono text-gray-600">({{ entity.entityId }})</span></span>
                </td>
                <td class="px-2 py-1 border">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="tag in entity.infotags"
                      :key="tag.id"
                      class="inline-block px-1 py-0.5 text-xs rounded"
                      :style="{ backgroundColor: tag.color || '#gray', color: '#fff' }"
                      :title="tag.description || tag.name"
                    >
                      {{ tag.name }}
                    </span>
                    <span v-if="!entity.infotags || entity.infotags.length === 0" class="text-gray-400 text-xs">-</span>
                  </div>
                </td>
                <td class="px-2 py-1 border">
                  <div class="flex flex-wrap gap-1">
                    <span
                      v-for="alert in entity.metricalerts"
                      :key="alert.metricAlertId"
                      class="inline-block px-1 py-0.5 text-xs rounded border"
                      :class="{
                        'bg-red-100 border-red-500 text-red-800': alert.severity === 'high' || alert.severity === 'critical',
                        'bg-yellow-100 border-yellow-500 text-yellow-800': alert.severity === 'medium' || alert.severity === 'warning',
                        'bg-blue-100 border-blue-500 text-blue-800': alert.severity === 'low' || alert.severity === 'info',
                        'bg-gray-100 border-gray-500 text-gray-800': !alert.severity
                      }"
                      :title="alert.description || alert.metricAlertName"
                    >
                      {{ alert.metricAlertName }}
                    </span>
                    <span v-if="!entity.metricalerts || entity.metricalerts.length === 0" class="text-gray-400 text-xs">-</span>
                  </div>
                </td>
                <td class="px-2 py-1 border text-right">
                  {{ entity.attribution2024.toLocaleString() }} <span class="text-gray-600 text-xs">({{ entity.numProviders !== null ? entity.numProviders : '-' }})</span>
                </td>
                <td class="px-2 py-1 border text-left">
                  <div v-if="entity.workloadDistribution" class="workload-tooltip-container">
                    <span
                      class="cursor-help"
                      @mouseenter="showTooltip($event, entity)"
                      @mouseleave="hideTooltip"
                    >
                      {{ entity.workloadDistribution }}
                    </span>
                  </div>
                  <span v-else>-</span>
                </td>
                <td class="px-2 py-1 border text-right" :class="entity.awvDeviation !== null && entity.awvDeviation > 0 ? 'text-green-600 font-semibold' : entity.awvDeviation !== null && entity.awvDeviation < 0 ? 'text-red-600 font-semibold' : ''">
                  <span>{{ entity.awvSlope !== null ? (entity.awvSlope >= 0 ? '+' : '') + (entity.awvSlope * 100).toFixed(2) + '%' : 'N/A' }}</span>
                  <span v-if="entity.awvPValue !== null && entity.awvPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
                </td>
                <td class="px-2 py-1 border text-right" :class="entity.attribDeviation !== null && entity.attribDeviation > 0 ? 'text-green-600 font-semibold' : entity.attribDeviation !== null && entity.attribDeviation < 0 ? 'text-red-600 font-semibold' : ''">
                  <span>{{ entity.attribSlope !== null ? (entity.attribSlope >= 0 ? '+' : '') + entity.attribSlope.toFixed(2) + '%' : 'N/A' }}</span>
                  <span v-if="entity.attribPValue !== null && entity.attribPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
                </td>
                <td class="px-2 py-1 border text-right" :class="entity.pmpyDeviation !== null && entity.pmpyDeviation < 0 ? 'text-green-600 font-semibold' : entity.pmpyDeviation !== null && entity.pmpyDeviation > 0 ? 'text-red-600 font-semibold' : ''">
                  <span>{{ entity.pmpySlope !== null ? (entity.pmpySlope >= 0 ? '+' : '') + '$' + entity.pmpySlope.toFixed(2) : 'N/A' }}</span>
                  <span v-if="entity.pmpyPValue !== null && entity.pmpyPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
                </td>
                <td class="px-2 py-1 border text-right" :class="entity.riskDeviation !== null && entity.riskDeviation > 0 ? 'text-green-600 font-semibold' : entity.riskDeviation !== null && entity.riskDeviation < 0 ? 'text-red-600 font-semibold' : ''">
                  <span>{{ entity.riskSlope !== null ? (entity.riskSlope >= 0 ? '+' : '') + entity.riskSlope.toFixed(3) : 'N/A' }}</span>
                  <span v-if="entity.riskPValue !== null && entity.riskPValue < 0.05" class="ml-1 text-yellow-600 font-bold" title="Statistically significant (p < 0.05)">*</span>
                </td>
                <td class="px-2 py-1 border text-right font-semibold" :class="entity.leverageIndex !== null && entity.leverageIndex < 0 ? 'text-green-600' : entity.leverageIndex !== null && entity.leverageIndex > 0 ? 'text-red-600' : ''">
                  <span>{{ entity.leverageIndex !== null ? '$' + entity.leverageIndex.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : 'N/A' }}</span>
                </td>
                <td class="px-2 py-1 border text-center">
                  <button
                    @click="openTagForm(entity)"
                    class="px-2 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                  >
                    Add Tag
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </div>

    <!-- Tag Form Modal -->
    <FormInfoTagRelationship
      v-if="showTagForm && entityToTag && entityToTag.entityDatabaseId"
      :entityId="entityToTag.entityDatabaseId"
      @close="closeTagForm"
      @update="handleTagFormUpdate"
    />
    <!-- Workload Tooltip (fixed position) -->
    <div
      v-if="tooltipVisible"
      class="workload-tooltip-fixed"
      :style="{ top: tooltipY + 'px', left: tooltipX + 'px' }"
    >
      <div><strong>Total Benes:</strong> {{ tooltipData.totalBenes?.toLocaleString() || 'N/A' }}</div>
      <div><strong>Avg Benes/Provider:</strong> {{ tooltipData.avgBenesPerProvider?.toFixed(1) || 'N/A' }}</div>
      <div><strong>Min Benes/Provider:</strong> {{ tooltipData.minBenesPerProvider?.toLocaleString() || 'N/A' }}</div>
      <div><strong>Max Benes/Provider:</strong> {{ tooltipData.maxBenesPerProvider?.toLocaleString() || 'N/A' }}</div>
      <div><strong>HHI (Concentration):</strong> {{ tooltipData.hhi !== null ? tooltipData.hhi.toFixed(3) : 'N/A' }}</div>
      <div class="text-xs text-gray-300 mt-1">Higher HHI = More concentrated = Easier to engage</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import FormInfoTagRelationship from '../forms/Form_InfoTagRelationship.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'Portfolio_Analysis_v2',
  components: {
    FormInfoTagRelationship,
    ComponentHeader,
    LoadingIndicator,
  },
  data() {
    return {
      portfolio_manager_data_raw: [], // Unfiltered data from backend
      attribution_balance_data: [], // Attribution balance data from backend
      showData: false,
      selectedContractFilter: '',
      selectedEntity: null, // Selected entity for Table 2
      nationalPmpyBenchmark: 800, // User-editable national PMPY slope benchmark
      avgNationalPmpy: 15591, // User-editable average national PMPY
      showTagForm: false, // Control visibility of tag form
      entityToTag: null, // Entity being tagged
      tooltipVisible: false, // Control visibility of workload tooltip
      tooltipX: 0, // Tooltip X position
      tooltipY: 0, // Tooltip Y position
      tooltipData: {}, // Tooltip data
      showRegressionSettings: false, // Control visibility of regression settings accordion
      showPortfolioSlopes: false // Control visibility of portfolio slopes accordion
    };
  },
  computed: {
    availableContracts() {
      if (!this.portfolio_manager_data_raw || this.portfolio_manager_data_raw.length === 0) {
        return [];
      }

      const contractMap = new Map();
      this.portfolio_manager_data_raw.forEach(item => {
        if (item.contract_id && !contractMap.has(item.contract_id)) {
          contractMap.set(item.contract_id, {
            id: item.contract_id,
            name: item.contract_name || item.contract_id
          });
        }
      });

      return Array.from(contractMap.values()).sort((a, b) =>
        (a.name || a.id).localeCompare(b.name || b.id)
      );
    },

    entityData() {
      if (!this.portfolio_manager_data_raw || this.portfolio_manager_data_raw.length === 0) {
        return {};
      }

      // Filter by contract if selected
      const filteredData = this.selectedContractFilter
        ? this.portfolio_manager_data_raw.filter(item => item.contract_id === this.selectedContractFilter)
        : this.portfolio_manager_data_raw;

      const entities = {};

      // Group all observations by entity
      filteredData.forEach(item => {
        const entityKey = item.tin || item.ccn || item.npi || item.contract_id;
        if (!entityKey) return;

        if (!entities[entityKey]) {
          // Parse infotags if it's a JSON string
          let infotags = item.infotags || [];
          if (typeof infotags === 'string') {
            try {
              infotags = JSON.parse(infotags);
            } catch (e) {
              infotags = [];
            }
          }

          // Parse metricalerts if it's a JSON string
          let metricalerts = item.metricalerts || [];
          if (typeof metricalerts === 'string') {
            try {
              metricalerts = JSON.parse(metricalerts);
            } catch (e) {
              metricalerts = [];
            }
          }

          // Initialize entity
          entities[entityKey] = {
            entityId: entityKey,
            entityDatabaseId: item.entity_id || item.id || null, // Actual database primary key
            entityType: item.tin ? 'TIN' : item.ccn ? 'CCN' : item.npi ? 'NPI' : 'CONTRACT',
            entityName: item.entity_name || null,
            infotags: infotags,
            metricalerts: metricalerts,
            observations: []
          };
        }

        // Add observation
        entities[entityKey].observations.push({
          observation_date: item.observation_date,
          year: item.observation_date ? parseInt(item.observation_date.substring(0, 4)) : null,
          awv_rate: item.awv_rate,
          attrib_benes: item.attrib_benes,
          total_pmpy: item.total_pmpy,
          avg_hcc_score_v28: item.avg_hcc_score_v28
        });
      });

      // Filter entities based on criteria
      const validEntities = {};

      Object.entries(entities).forEach(([entityKey, entity]) => {
        // Check if entity has 2022, 2023, and 2024 data with all required non-null values
        const requiredYears = [2022, 2023, 2024];
        const yearData = {};

        entity.observations.forEach(obs => {
          if (requiredYears.includes(obs.year)) {
            yearData[obs.year] = obs;
          }
        });

        // Must have all three years
        if (Object.keys(yearData).length !== 3) return;

        // Check that all three years have non-null values for required fields
        for (const year of requiredYears) {
          const data = yearData[year];
          if (!data ||
              data.awv_rate === null || data.awv_rate === undefined ||
              data.attrib_benes === null || data.attrib_benes === undefined ||
              data.total_pmpy === null || data.total_pmpy === undefined ||
              data.avg_hcc_score_v28 === null || data.avg_hcc_score_v28 === undefined) {
            return;
          }
        }

        // Filter observations to only include those with all required non-null values
        // Allow 2025 data even if it has null values (it's display-only, not used in calculations)
        entity.observations = entity.observations.filter(obs => {
          // For 2025, allow null values since it's just for display
          if (obs.year === 2025) {
            return obs.awv_rate !== null && obs.awv_rate !== undefined &&
                   obs.attrib_benes !== null && obs.attrib_benes !== undefined &&
                   obs.total_pmpy !== null && obs.total_pmpy !== undefined;
          }
          // For other years, require all fields
          return obs.awv_rate !== null && obs.awv_rate !== undefined &&
                 obs.attrib_benes !== null && obs.attrib_benes !== undefined &&
                 obs.total_pmpy !== null && obs.total_pmpy !== undefined &&
                 obs.avg_hcc_score_v28 !== null && obs.avg_hcc_score_v28 !== undefined;
        });

        // Sort observations by year
        entity.observations.sort((a, b) => a.year - b.year);

        // Calculate regression slopes for each metric
        // Using linear regression: y = slope * time + intercept
        // Always use 2022-2024 for regression
        const regressionObs = entity.observations.filter(obs => obs.year >= 2022 && obs.year <= 2024);

        const n = regressionObs.length;
        if (n >= 2) {
          const timeIndices = regressionObs.map((_, i) => i);
          const awvValues = regressionObs.map(obs => obs.awv_rate);
          const attribValues = regressionObs.map(obs => obs.attrib_benes);
          const pmpyValues = regressionObs.map(obs => obs.total_pmpy);
          const riskValues = regressionObs.map(obs => obs.avg_hcc_score_v28);

          // Calculate sums needed for regression
          const sumTime = timeIndices.reduce((a, b) => a + b, 0);
          const sumTimeSq = timeIndices.reduce((a, b) => a + b * b, 0);
          const meanTime = sumTime / n;

          // Helper function to calculate slope and p-value
          const calculateSlopeAndPValue = (values) => {
            const sumValue = values.reduce((a, b) => a + b, 0);
            const sumTimeValue = timeIndices.reduce((sum, time, i) => sum + time * values[i], 0);
            const slope = (n * sumTimeValue - sumTime * sumValue) / (n * sumTimeSq - sumTime * sumTime);

            // Calculate p-value
            if (n >= 3) {
              const meanValue = sumValue / n;
              const intercept = meanValue - slope * meanTime;

              // Calculate residuals and MSE
              let sumSquaredResiduals = 0;
              timeIndices.forEach((time, i) => {
                const predicted = slope * time + intercept;
                const residual = values[i] - predicted;
                sumSquaredResiduals += residual * residual;
              });

              const mse = sumSquaredResiduals / (n - 2);
              const sumSquaredDeviations = timeIndices.reduce((sum, time) => sum + (time - meanTime) ** 2, 0);
              const standardError = Math.sqrt(mse / sumSquaredDeviations);

              // t-statistic
              const tStat = Math.abs(slope / standardError);

              // Calculate p-value from t-distribution with df = n - 2
              const df = n - 2;
              const pValue = this.calculatePValue(tStat, df);

              return { slope, pValue };
            }

            return { slope, pValue: null };
          };

          const awvResult = calculateSlopeAndPValue(awvValues);
          const attribResult = calculateSlopeAndPValue(attribValues);
          const pmpyResult = calculateSlopeAndPValue(pmpyValues);
          const riskResult = calculateSlopeAndPValue(riskValues);

          // Get 2024 data (last observation)
          const data2024 = entity.observations[entity.observations.length - 1];

          entity.awvSlope = awvResult.slope;
          entity.awvPValue = awvResult.pValue;
          // Convert attribution slope to percentage of 2024 attribution
          entity.attribSlope = data2024.attrib_benes > 0
            ? (attribResult.slope / data2024.attrib_benes) * 100
            : null;
          entity.attribPValue = attribResult.pValue;
          entity.pmpySlope = pmpyResult.slope;
          entity.pmpyPValue = pmpyResult.pValue;
          entity.riskSlope = riskResult.slope;
          entity.riskPValue = riskResult.pValue;
        } else {
          entity.awvSlope = null;
          entity.awvPValue = null;
          entity.attribSlope = null;
          entity.attribPValue = null;
          entity.pmpySlope = null;
          entity.pmpyPValue = null;
          entity.riskSlope = null;
          entity.riskPValue = null;
        }

        validEntities[entityKey] = entity;
      });

      return validEntities;
    },

    portfolioAverageSlopes() {
      // Calculate portfolio-level regression slopes
      // This aggregates all entity observations and runs regression on the portfolio level
      const entities = Object.values(this.entityData);
      if (entities.length === 0) return null;

      // Collect all observations across all entities grouped by year
      const yearData = {};

      entities.forEach(entity => {
        entity.observations.forEach(obs => {
          // Only use 2022-2024 for regression
          if (obs.year < 2022 || obs.year > 2024) {
            return; // Skip years outside 2022-2024 range
          }

          if (!yearData[obs.year]) {
            yearData[obs.year] = {
              totalAwv: 0,
              totalAttrib: 0,
              totalPmpy: 0,
              totalRisk: 0,
              totalWeight: 0,
              count: 0
            };
          }

          const weight = obs.attrib_benes || 0;
          yearData[obs.year].totalAwv += obs.awv_rate * weight;
          yearData[obs.year].totalAttrib += obs.attrib_benes;
          yearData[obs.year].totalPmpy += obs.total_pmpy * weight;
          yearData[obs.year].totalRisk += obs.avg_hcc_score_v28 * weight;
          yearData[obs.year].totalWeight += weight;
          yearData[obs.year].count++;
        });
      });

      // Calculate weighted averages for each year
      const years = Object.keys(yearData).map(Number).sort();
      if (years.length < 2) return null;

      const awvValues = years.map(year => yearData[year].totalAwv / yearData[year].totalWeight);
      const attribValues = years.map(year => yearData[year].totalAttrib);
      const pmpyValues = years.map(year => yearData[year].totalPmpy / yearData[year].totalWeight);
      const riskValues = years.map(year => yearData[year].totalRisk / yearData[year].totalWeight);

      // Run regression on portfolio-level data
      const n = years.length;
      const timeIndices = years.map((_, i) => i);
      const sumTime = timeIndices.reduce((a, b) => a + b, 0);
      const sumTimeSq = timeIndices.reduce((a, b) => a + b * b, 0);
      const meanTime = sumTime / n;

      const calculateSlopeAndPValue = (values) => {
        const sumValue = values.reduce((a, b) => a + b, 0);
        const sumTimeValue = timeIndices.reduce((sum, time, i) => sum + time * values[i], 0);
        const slope = (n * sumTimeValue - sumTime * sumValue) / (n * sumTimeSq - sumTime * sumTime);

        if (n >= 3) {
          const meanValue = sumValue / n;
          const intercept = meanValue - slope * meanTime;

          let sumSquaredResiduals = 0;
          timeIndices.forEach((time, i) => {
            const predicted = slope * time + intercept;
            const residual = values[i] - predicted;
            sumSquaredResiduals += residual * residual;
          });

          const mse = sumSquaredResiduals / (n - 2);
          const sumSquaredDeviations = timeIndices.reduce((sum, time) => sum + (time - meanTime) ** 2, 0);
          const standardError = Math.sqrt(mse / sumSquaredDeviations);
          const tStat = Math.abs(slope / standardError);
          const df = n - 2;
          const pValue = this.calculatePValue(tStat, df);

          return { slope, pValue };
        }

        return { slope, pValue: null };
      };

      const awvResult = calculateSlopeAndPValue(awvValues);
      const attribResult = calculateSlopeAndPValue(attribValues);
      const pmpyResult = calculateSlopeAndPValue(pmpyValues);
      const riskResult = calculateSlopeAndPValue(riskValues);

      // Convert attribution slope to percentage of 2024 attribution
      const attrib2024 = yearData[2024]?.totalAttrib || 1;
      const attribSlopePercent = (attribResult.slope / attrib2024) * 100;

      return {
        awv: awvResult.slope,
        awvPValue: awvResult.pValue,
        attrib: attribSlopePercent,
        attribPValue: attribResult.pValue,
        pmpy: pmpyResult.slope,
        pmpyPValue: pmpyResult.pValue,
        risk: riskResult.slope,
        riskPValue: riskResult.pValue
      };
    },

    entityList() {
      // Convert entityData object to array for table display
      // Add deviation from portfolio average for each metric
      // Add leverage index calculation
      // Sort by leverage index (best to worst)
      const entities = Object.values(this.entityData);

      if (!this.portfolioAverageSlopes) return entities;

      const entitiesWithMetrics = entities.map(entity => {
        // Get 2024 attribution (last observation)
        const data2024 = entity.observations[entity.observations.length - 1];
        const attribution = data2024?.attrib_benes || 0;

        // Calculate leverage index
        // Leverage Index = (PMPY slope - national benchmark) * attribution - (risk slope * avg national PMPY * attribution)
        let leverageIndex = null;
        if (entity.pmpySlope !== null && entity.riskSlope !== null && attribution > 0) {
          const pmpyDelta = entity.pmpySlope - this.nationalPmpyBenchmark;
          const riskAdjustment = entity.riskSlope * this.avgNationalPmpy;
          leverageIndex = (pmpyDelta * attribution) - (riskAdjustment * attribution);
        }

        // Look up attribution balance data
        const balanceData = this.attribution_balance_data.find(
          item => item.entity_id === entity.entityDatabaseId
        );

        // Calculate workload distribution category from raw values
        const workloadDistribution = balanceData ? this.categorizeWorkloadDistribution(balanceData) : null;

        return {
          ...entity,
          awvDeviation: entity.awvSlope !== null ? entity.awvSlope - this.portfolioAverageSlopes.awv : null,
          attribDeviation: entity.attribSlope !== null ? entity.attribSlope - this.portfolioAverageSlopes.attrib : null,
          pmpyDeviation: entity.pmpySlope !== null ? entity.pmpySlope - this.portfolioAverageSlopes.pmpy : null,
          riskDeviation: entity.riskSlope !== null ? entity.riskSlope - this.portfolioAverageSlopes.risk : null,
          leverageIndex: leverageIndex,
          attribution2024: attribution,
          numProviders: balanceData?.num_providers || null,
          totalBenes: balanceData?.total_benes || null,
          avgBenesPerProvider: balanceData?.avg_benes_per_provider || null,
          minBenesPerProvider: balanceData?.min_benes_per_provider || null,
          maxBenesPerProvider: balanceData?.max_benes_per_provider || null,
          hhi: balanceData?.hhi || null,
          workloadDistribution: workloadDistribution
        };
      });

      // Sort by leverage index: most positive (worst) to most negative (best)
      return entitiesWithMetrics.sort((a, b) => {
        if (a.leverageIndex === null && b.leverageIndex === null) return 0;
        if (a.leverageIndex === null) return 1;
        if (b.leverageIndex === null) return -1;
        return b.leverageIndex - a.leverageIndex;
      });
    },

    metricsTableData() {
      // Generate table data for Table 2
      // If entity is selected, show that entity's data
      // Otherwise, show weighted average across all entities

      if (this.selectedEntity) {
        // Show selected entity's observations
        return this.selectedEntity.observations.map(obs => ({
          year: obs.year,
          awv_rate: obs.awv_rate,
          attribution: obs.attrib_benes,
          total_pmpy: obs.total_pmpy,
          risk: obs.avg_hcc_score_v28
        }));
      } else {
        // Calculate weighted averages across all entities for each year
        const yearMap = new Map();

        Object.values(this.entityData).forEach(entity => {
          entity.observations.forEach(obs => {
            if (!yearMap.has(obs.year)) {
              yearMap.set(obs.year, {
                year: obs.year,
                totalAwvWeighted: 0,
                totalAttribution: 0,
                totalPmpyWeighted: 0,
                totalRiskWeighted: 0,
                totalWeight: 0,
                riskWeight: 0  // Track weight for risk separately to handle null values
              });
            }

            const yearData = yearMap.get(obs.year);
            const weight = obs.attrib_benes;

            yearData.totalAwvWeighted += obs.awv_rate * weight;
            yearData.totalAttribution += obs.attrib_benes;
            yearData.totalPmpyWeighted += obs.total_pmpy * weight;
            // Only add risk if it's not null (2025 data may have null risk)
            if (obs.avg_hcc_score_v28 !== null && obs.avg_hcc_score_v28 !== undefined) {
              yearData.totalRiskWeighted += obs.avg_hcc_score_v28 * weight;
              yearData.riskWeight += weight;
            }
            yearData.totalWeight += weight;
          });
        });

        // Convert to array and calculate weighted averages
        const result = Array.from(yearMap.values()).map(yearData => ({
          year: yearData.year,
          awv_rate: yearData.totalAwvWeighted / yearData.totalWeight,
          attribution: yearData.totalAttribution,
          total_pmpy: yearData.totalPmpyWeighted / yearData.totalWeight,
          risk: yearData.riskWeight > 0 ? yearData.totalRiskWeighted / yearData.riskWeight : null
        }));

        // Sort by year
        return result.sort((a, b) => a.year - b.year);
      }
    },

    awvPmpyRegressionAnalysis() {
      // Regress change in AWV performance with change in PMPY across 2022-2024
      // Returns slope, R-squared, p-value, and other regression statistics
      const entities = Object.values(this.entityData);

      if (entities.length < 2) return null;

      // Collect data points: each entity contributes one point
      // X = change in AWV (2024 - 2022)
      // Y = change in PMPY (2024 - 2022)
      const dataPoints = [];

      entities.forEach(entity => {
        const obs = entity.observations;
        const data2022 = obs.find(o => o.year === 2022);
        const data2024 = obs.find(o => o.year === 2024);

        if (!data2022 || !data2024) return;

        const awvChange = data2024.awv_rate - data2022.awv_rate;
        const pmpyChange = data2024.total_pmpy - data2022.total_pmpy;

        dataPoints.push({
          entityId: entity.entityId,
          entityName: entity.entityName,
          entityType: entity.entityType,
          awvChange: awvChange,
          pmpyChange: pmpyChange
        });
      });

      if (dataPoints.length < 2) return null;

      // Calculate regression: Y = slope * X + intercept
      const n = dataPoints.length;
      const xValues = dataPoints.map(d => d.awvChange);
      const yValues = dataPoints.map(d => d.pmpyChange);

      const sumX = xValues.reduce((a, b) => a + b, 0);
      const sumY = yValues.reduce((a, b) => a + b, 0);
      const sumXY = xValues.reduce((sum, x, i) => sum + x * yValues[i], 0);
      const sumXX = xValues.reduce((sum, x) => sum + x * x, 0);
      const sumYY = yValues.reduce((sum, y) => sum + y * y, 0);

      const meanX = sumX / n;
      const meanY = sumY / n;

      // Calculate slope and intercept
      const numerator = n * sumXY - sumX * sumY;
      const denominator = n * sumXX - sumX * sumX;

      if (denominator === 0) return null;

      const slope = numerator / denominator;
      const intercept = meanY - slope * meanX;

      // Calculate R-squared
      const ssTotal = yValues.reduce((sum, y) => sum + (y - meanY) ** 2, 0);
      const ssResidual = yValues.reduce((sum, y, i) => {
        const predicted = slope * xValues[i] + intercept;
        return sum + (y - predicted) ** 2;
      }, 0);
      const rSquared = ssTotal > 0 ? 1 - (ssResidual / ssTotal) : 0;

      // Calculate standard error and t-statistic
      const mse = ssResidual / (n - 2);
      const ssX = xValues.reduce((sum, x) => sum + (x - meanX) ** 2, 0);
      const standardError = Math.sqrt(mse / ssX);
      const tStat = Math.abs(slope / standardError);

      // Calculate p-value
      const df = n - 2;
      const pValue = this.calculatePValue(tStat, df);

      // Determine if statistically significant
      const isSignificant = pValue < 0.05;

      return {
        slope: slope,
        intercept: intercept,
        rSquared: rSquared,
        pValue: pValue,
        isSignificant: isSignificant,
        sampleSize: n,
        dataPoints: dataPoints
      };
    }
  },
  methods: {
    calculatePValue(t, df) {
      // Two-tailed t-test p-value calculation
      // For small df, use approximation
      if (df <= 0) return 1;

      // Student's t-distribution CDF approximation
      const x = df / (df + t * t);
      let p;

      if (df === 1) {
        p = 1 - (2 / Math.PI) * Math.atan(Math.abs(t));
      } else if (df === 2) {
        p = 1 - Math.abs(t) / Math.sqrt(2 + t * t);
      } else {
        // Use normal approximation for df > 2
        const z = t;
        p = 2 * (1 - this.normalCDF(Math.abs(z)));
      }

      return Math.min(Math.max(p, 0), 1);
    },

    normalCDF(z) {
      // Standard normal cumulative distribution function
      const t = 1 / (1 + 0.2316419 * Math.abs(z));
      const d = 0.3989423 * Math.exp(-z * z / 2);
      const prob = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
      return z > 0 ? 1 - prob : prob;
    },

    categorizeWorkloadDistribution(balanceData) {
      // Categorize based on HHI (Herfindahl-Hirschman Index)
      // HHI measures concentration: Higher = More concentrated = Easier to engage
      // Provider count doesn't matter - concentration does!
      const numProviders = balanceData.num_providers || 0;
      const totalBenes = balanceData.total_benes || 0;
      const avgBenesPerProvider = balanceData.avg_benes_per_provider || 0;
      const hhi = balanceData.hhi; // 0 to 1 scale

      // Deal-breakers - too small to bother
      if (totalBenes < 50) {
        return '🔴 Too Small - Not Worth It';
      }
      if (avgBenesPerProvider < 25) {
        return '🔴 Avg Panel Too Small';
      }

      // EXCELLENT: Juicy targets exist (HHI > 0.40)
      // High concentration = Few providers dominate = Easy to engage
      if (hhi >= 0.40) {
        return '🟢 Excellent - Juicy Targets';
      }
      if (hhi >= 0.30 && numProviders <= 10) {
        return '🟢 Great - Well Concentrated';
      }

      // GOOD: Moderately concentrated (HHI 0.20-0.30)
      // Some concentration exists, manageable
      if (hhi >= 0.25) {
        return '🟡 Good - Moderately Concentrated';
      }
      if (hhi >= 0.20 && numProviders <= 15) {
        return '🟡 OK - Manageable';
      }

      // PROBLEMATIC: Cat herding (HHI < 0.15)
      // Low concentration = Fragmented = Hard to engage
      // Note: Provider count doesn't matter - HHI captures the real issue
      if (hhi !== null && hhi < 0.15) {
        return '🔴 Highly Fragmented';
      }

      // Default
      return '🟡 Moderate Fragmentation';
    },

    async fetch_portfolio_manager_data() {
      const request_data = {
        sql_filters: {},
      };

      try {
        const response = await axios.post(
          'api/portfolio_management/insights/',
          request_data
        );

        if (response.data && response.data.length > 0) {
          this.portfolio_manager_data_raw = response.data;
          this.showData = true;
        } else {
          this.showData = false;
        }
      } catch (error) {
        console.error('Error fetching portfolio manager data:', error);
        this.showData = false;
      }
    },

    async fetch_attribution_balance_data() {
      try {
        const response = await axios.post(
          'api/portfolio_management/attribution-balance/',
          {}
        );

        if (response.data && response.data.length > 0) {
          this.attribution_balance_data = response.data;
        }
      } catch (error) {
        console.error('Error fetching attribution balance data:', error);
        this.attribution_balance_data = [];
      }
    },

    selectEntity(entity) {
      this.selectedEntity = entity;
    },

    openTagForm(entity) {
      if (!entity.entityDatabaseId) {
        console.error('Entity database ID not found. Entity:', entity);
        alert('Unable to add tag: Entity database ID not found. Please check the console for details.');
        return;
      }
      this.entityToTag = entity;
      this.showTagForm = true;
    },

    closeTagForm() {
      this.showTagForm = false;
      this.entityToTag = null;
    },

    handleTagFormUpdate() {
      // Refresh the data to show the newly added tag
      this.fetch_portfolio_manager_data();
      this.closeTagForm();
    },

    showTooltip(event, entity) {
      const rect = event.target.getBoundingClientRect();
      this.tooltipX = rect.right + 10; // Position to the right of the element
      this.tooltipY = rect.top - 20; // Align with the top of the element
      this.tooltipData = {
        totalBenes: entity.totalBenes,
        avgBenesPerProvider: entity.avgBenesPerProvider,
        minBenesPerProvider: entity.minBenesPerProvider,
        maxBenesPerProvider: entity.maxBenesPerProvider,
        hhi: entity.hhi
      };
      this.tooltipVisible = true;
    },

    hideTooltip() {
      this.tooltipVisible = false;
    }
  },
  mounted() {
    this.fetch_portfolio_manager_data();
    this.fetch_attribution_balance_data();
  },
};
</script>

<style scoped>
.workload-tooltip-container {
  display: inline-block;
}

.workload-tooltip-fixed {
  position: fixed;
  z-index: 9999;
  background-color: #1f2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 11px;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.workload-tooltip-fixed div {
  margin-bottom: 4px;
  text-align: left;
}

.workload-tooltip-fixed div:last-child {
  margin-bottom: 0;
}
</style>
